// src/components/documents/LocationAutocomplete.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchAddress } from '../../services/geocoding';

export default function LocationAutocomplete({ value, onSelect, placeholder, icon = '📍' }) {
  const [query, setQuery] = useState(value?.address || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // Keep local text in sync if parent resets the value externally
  useEffect(() => { setQuery(value?.address || ''); }, [value?.address]);

  // Close dropdown on outside click
  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const runSearch = useCallback((text) => {
    clearTimeout(debounceRef.current);
    if (!text || text.trim().length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const results = await searchAddress(text);
      setSuggestions(results);
      setLoading(false);
      setOpen(true);
      setHighlighted(-1);
    }, 400); // debounce so we don't hammer Nominatim on every keystroke
  }, []);

  const handleChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    runSearch(text);
  };

  const handleSelect = (s) => {
    setQuery(s.address);
    setOpen(false);
    setSuggestions([]);
    onSelect({ address: s.address, lat: s.lat, lng: s.lng, fullLabel: s.label, approximate: s.approximate });
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (highlighted >= 0) handleSelect(suggestions[highlighted]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, pointerEvents: 'none' }}>{icon}</span>
        <input
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          style={{
            background: 'var(--bgInput)', border: '1.5px solid var(--borderInput)', borderRadius: 9,
            padding: '10px 12px 10px 32px', color: 'var(--textPrimary)', fontSize: 13, outline: 'none',
            width: '100%', fontFamily: 'DM Sans, sans-serif',
          }}
        />
        {loading && (
          <span style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--textFaint)' }}>
            ⏳
          </span>
        )}
        {value?.lat && !loading && (
          <span style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#10b981' }}>
            ✓
          </span>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 9999,
          background: 'var(--bgModal)', border: '1px solid var(--borderInput)', borderRadius: 10,
          boxShadow: 'var(--shadow)', maxHeight: 260, overflowY: 'auto',
        }}>
          {suggestions[0]?.approximate && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: '#f59e0b', background: 'rgba(245,158,11,0.08)', borderBottom: '1px solid var(--border)' }}>
              ⚠ No exact match for that full address — showing the closest known area instead. Pick one, then click the map below to fine-tune the exact spot.
            </div>
          )}
          {suggestions.map((s, i) => (
            <div
              key={i}
              onClick={() => handleSelect(s)}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                padding: '10px 12px', cursor: 'pointer', fontSize: 12.5,
                background: highlighted === i ? 'var(--bgActive)' : 'transparent',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
                color: 'var(--textSecondary)', display: 'flex', gap: 8, alignItems: 'flex-start',
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 1 }}>{s.approximate ? '🔶' : '📍'}</span>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {open && !loading && suggestions.length === 0 && query.trim().length >= 3 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 9999,
          background: 'var(--bgModal)', border: '1px solid var(--borderInput)', borderRadius: 10,
          padding: '10px 12px', fontSize: 12, color: 'var(--textFaint)',
        }}>
          No matching locations found
        </div>
      )}
    </div>
  );
}
